// Monthly posts board (feature 007-monthly-posts-view). A presentational month grid that replaces
// FullCalendar's dayGridMonth: a toolbar (ideas/prev/next/centered month title/view switch), a
// localized Mon..Sun weekday header, and a 7-column × 6-week grid of day cells. Each cell renders up
// to 3 MonthPostCards and a "+N more" roll-up for the rest. Each day cell body is a drop target and
// each visible card is draggable (US3 drag-to-reschedule), mirroring WeekPostsBoard's rbd setup.
import React from "react";
import {addDays, format} from "date-fns";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {convertToMomentLocale} from "../../helpers/functions";
import MonthPostCard from "./MonthPostCard";
import VariantGroupCard from "./VariantGroupCard";
import {groupsForDay} from "./VariantGroupCard.helpers";
import {VariantGroupType} from "../../redux/Content/VariantGroup/types";
import {buildMonthGrid, formatDroppableId, MonthDayCell, parseDroppableId} from "./MonthPostsBoard.helpers";
import {isRescheduleLocked} from "./WeekPostCard.helpers";

type Translate = (key: string, defaultValue?: string) => string;

interface Props {
    activities: PeerActivityType[];
    cursor: Date;
    language: string;
    t: Translate;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onChangeView: (view: string) => void;
    onToggleIdeas: () => void;
    onEdit: (activity: PeerActivityType) => void;
    onDuplicate?: (activity: PeerActivityType) => void;
    onDelete?: (activity: PeerActivityType) => void;
    onRetry?: (activity: PeerActivityType) => void;
    // Accepted for API parity; drag-to-reschedule lands in US3.
    onReschedule?: (uuid: string, targetDay: Date) => void;
    onAddOnDay?: (day: Date) => void;
    onOpenDay?: (day: Date) => void;
    // Native HTML5 drop of an ideas-sidebar card onto a day cell → create a post from that idea.
    onIdeaDrop?: (uuid: string, date: Date) => void;
    // Feature 012: planned slots with alternative versions, one card each, and how to open one.
    groups?: VariantGroupType[];
    onOpenGroup?: (group: VariantGroupType) => void;
}

const IDEA_MIME = 'application/x-m9s-idea';

const stop = (e: React.MouseEvent) => e.stopPropagation();

// Drag-end decision, exported so it can be unit-tested with a crafted DropResult (react-beautiful-dnd
// does not simulate real pointer DnD under jsdom). Mirrors WeekPostsBoard.handleDragEnd: ignore a drop
// with no destination and a same-day drop, otherwise parse the destination droppableId (an ISO
// yyyy-MM-dd day) back into a Date and reschedule the dragged post (its uuid) onto it.
export function handleMonthDragEnd(
    result: DropResult,
    onReschedule?: (uuid: string, targetDay: Date) => void,
): void {
    if (!result.destination) return;
    if (result.source.droppableId === result.destination.droppableId) return;
    const target = parseDroppableId(result.destination.droppableId);
    if (!target) return;
    onReschedule?.(result.draggableId, target);
}

const MonthPostsBoard: React.FC<Props> = (props) => {
    const {
        activities, cursor, language, t,
        onPrev, onNext, onToday, onChangeView, onToggleIdeas,
        onEdit, onDuplicate, onDelete, onRetry, onAddOnDay, onOpenDay, onReschedule, onIdeaDrop, groups, onOpenGroup,
    } = props;
    const locale = convertToMomentLocale(language);

    // Which cell a native idea-drag is currently over (affordance only). Keyed by the cell's ISO date.
    const [ideaOverKey, setIdeaOverKey] = React.useState<string | null>(null);

    const handleDragEnd = (result: DropResult) => handleMonthDragEnd(result, onReschedule);

    // Map an activity (by uuid) back from a card model so the card callbacks can fire the real flows.
    const byUuid = new Map<string, PeerActivityType>();
    (activities || []).forEach((a) => {
        if (a.uuid) byUuid.set(a.uuid, a);
    });
    const withActivity = (uuid: string, fn?: (a: PeerActivityType) => void) => {
        const a = byUuid.get(uuid);
        if (a && fn) fn(a);
    };

    const grid = buildMonthGrid(cursor, activities, {weekStartsOn: 1, limit: 3, t, language});
    const title = format(cursor, "LLLL yyyy", {locale});

    // Mon..Sun weekday header, localized via the date-fns locale (rangeStart is a Monday).
    const weekdayLabels: string[] = Array.from({length: 7}, (_, i) =>
        format(addDays(grid.rangeStart, i), "EEE", {locale}),
    );

    const openDay = (day: Date) => {
        if (onOpenDay) onOpenDay(day);
        else onChangeView("timeGridDay");
    };

    const renderCell = (cell: MonthDayCell) => {
        const cellKey = cell.date.toISOString();
        const cellGroups = groupsForDay(groups, cell.date);
        const cellClass = "monthBoard__cell"
            + (cell.isToday ? " monthBoard__cell--today" : "")
            + (cell.isWeekend ? " monthBoard__cell--weekend" : "")
            + (cell.isOutOfMonth ? " monthBoard__cell--out" : "")
            + (ideaOverKey === cellKey ? " monthBoard__cell--ideaover" : "");

        // Native idea-drop target (separate event system from the rbd card reschedule below and from
        // the empty-cell onAddOnDay click, so neither interferes).
        const onDragOver = (e: React.DragEvent) => {
            if (!onIdeaDrop) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        };
        const onDrop = (e: React.DragEvent) => {
            const uuid = e.dataTransfer.getData(IDEA_MIME);
            setIdeaOverKey(null);
            if (uuid) {
                e.preventDefault();
                onIdeaDrop?.(uuid, cell.date);
            }
        };

        return (
            <div
                className={cellClass}
                key={cellKey}
                onClick={() => onAddOnDay?.(cell.date)}
                onDragOver={onDragOver}
                onDragEnter={() => onIdeaDrop && setIdeaOverKey(cellKey)}
                onDragLeave={(e) => {
                    // Ignore leaves into child nodes; only clear when leaving the cell itself.
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setIdeaOverKey((k) => (k === cellKey ? null : k));
                }}
                onDrop={onDrop}
            >
                <div className="monthBoard__cellHead">
                    <span className="monthBoard__dayNum">{cell.dayNumber}</span>
                </div>

                {cellGroups.length > 0 &&
                    <div className="monthBoard__groups" onClick={stop}>
                        {cellGroups.map((group) => <VariantGroupCard key={group.uuid} group={group} t={t} onOpen={onOpenGroup} compact/>)}
                    </div>}

                <Droppable droppableId={formatDroppableId(cell.date)}>
                    {(dropProvided, dropSnapshot) => (
                        <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className={"monthBoard__cellBody" + (dropSnapshot.isDraggingOver ? " monthBoard__cellBody--over" : "")}
                            onClick={stop}
                        >
                            {cell.visiblePosts.map((model, idx) => {
                                const dragLocked = isRescheduleLocked(model.status);
                                return (
                                <Draggable key={model.uuid} draggableId={model.uuid} index={idx} isDragDisabled={dragLocked}>
                                    {(dragProvided, dragSnapshot) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            className={"monthBoard__cardWrap"
                                                + (dragSnapshot.isDragging ? " monthBoard__cardWrap--dragging" : "")
                                                + (dragLocked ? " monthBoard__cardWrap--locked" : "")}
                                        >
                                            <MonthPostCard
                                                model={model}
                                                onEdit={() => withActivity(model.uuid, onEdit)}
                                                onDuplicate={onDuplicate ? () => withActivity(model.uuid, onDuplicate) : undefined}
                                                onDelete={onDelete ? () => withActivity(model.uuid, onDelete) : undefined}
                                                onRetry={onRetry ? () => withActivity(model.uuid, onRetry) : undefined}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                                );
                            })}

                            {/* free day (today/future, nothing scheduled): hover reveals a "Schedule"
                                placeholder as a clear affordance to create a post on this day. */}
                            {cell.posts.length === 0 && cellGroups.length === 0 && !cell.isPast && onAddOnDay &&
                                <button
                                    type="button"
                                    className="monthBoard__schedule"
                                    onClick={(e) => {stop(e); onAddOnDay(cell.date);}}
                                >
                                    <i className="ri-add-line" aria-hidden="true"/>
                                    {t("peerActivity:schedule", "Schedule")}
                                </button>}

                            {cell.overflowCount > 0 &&
                                <button
                                    type="button"
                                    className={"monthBoard__more" + (cell.overflowHasFailed ? " monthBoard__more--fail" : "")}
                                    onClick={(e) => {stop(e); openDay(cell.date);}}
                                >
                                    {cell.overflowHasFailed &&
                                        <span className="monthBoard__moreFail" title={t("peerActivityStatus:E", "Failed")} aria-hidden="true"/>}
                                    {t("peerActivity:nMore", "+{{n}} more").replace("{{n}}", String(cell.overflowCount))}
                                </button>}

                            {dropProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    return (
        <div className="monthBoard">
            <div className="monthBoard__toolbar">
                <button type="button" className="monthBoard__icbtn monthBoard__icbtn--pink" title={t("menu:activitiesIdeas")} onClick={onToggleIdeas}>
                    <i className="mdi mdi-diamond-stone"/>
                </button>
                <div className="monthBoard__nav">
                    <button type="button" className="monthBoard__icbtn" aria-label="Previous month" onClick={onPrev}><i className="ri-arrow-left-s-line"/></button>
                    <div className="monthBoard__title">{title}</div>
                    <button type="button" className="monthBoard__icbtn" aria-label="Next month" onClick={onNext}><i className="ri-arrow-right-s-line"/></button>
                </div>
                <div className="monthBoard__seg">
                    <button type="button" className="monthBoard__segBtn" onClick={onToday}>{t("common:today", "Today")}</button>
                    <button type="button" className="monthBoard__segBtn" onClick={() => onChangeView("timeGridDay")}>{t("common:day", "Day")}</button>
                    <button type="button" className="monthBoard__segBtn" onClick={() => onChangeView("timeGridWeek")}>{t("common:week", "Week")}</button>
                    <button type="button" className="monthBoard__segBtn monthBoard__segBtn--on" onClick={() => onChangeView("dayGridMonth")}>{t("common:month", "Month")}</button>
                </div>
            </div>

            <div className="monthBoard__grid">
                <div className="monthBoard__weekdays">
                    {weekdayLabels.map((label, i) => (
                        <div className="monthBoard__weekday" key={i}>{label}</div>
                    ))}
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                    {grid.weeks.map((week, wi) => (
                        <div className="monthBoard__week" key={wi}>
                            {week.days.map(renderCell)}
                        </div>
                    ))}
                </DragDropContext>
            </div>
        </div>
    );
};

export default MonthPostsBoard;
